import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Eye, Clock, User, Calendar, Phone, Trash2, Send, XCircle, Camera } from "lucide-react";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import useBulkSelect from "../../lib/useBulkSelect";
import BulkActionBar, { BulkCheckbox } from "../../components/BulkActionBar";

const CATEGORY_LABEL = {
  damage: "Danos no alojamento",
  consumables: "Falta de consumíveis",
  lightbulb: "Lâmpadas fundidas",
  maintenance: "Manutenção",
  extra_cleaning: "Limpeza extra necessária",
  other: "Outro",
};

const STATUS_STYLES = {
  open:              { color: "text-[#f59e0b]", border: "border-[#f59e0b]/40", bg: "bg-[#f59e0b]/5", label: "Reportado", icon: Clock },
  acknowledged:      { color: "text-[#3b82f6]", border: "border-[#3b82f6]/40", bg: "bg-[#3b82f6]/5", label: "Recebido",  icon: Eye },
  pending_client:    { color: "text-[#d4af37]", border: "border-[#d4af37]/40", bg: "bg-[#d4af37]/5", label: "Pendente cliente", icon: Send },
  client_approved:   { color: "text-[#10b981]", border: "border-[#10b981]/40", bg: "bg-[#10b981]/5", label: "Cliente aprovou", icon: CheckCircle2 },
  client_rejected:   { color: "text-[#fca5a5]", border: "border-[#ef4444]/40", bg: "bg-[#ef4444]/5", label: "Cliente recusou", icon: XCircle },
  resolved:          { color: "text-[#10b981]", border: "border-[#10b981]/40", bg: "bg-[#10b981]/5", label: "Resolvido",      icon: CheckCircle2 },
};

const FILTERS = [
  { key: "open", label: "Reportados" },
  { key: "acknowledged", label: "Recebidos" },
  { key: "pending_client", label: "Aguarda cliente" },
  { key: "client_approved", label: "Aprovados" },
  { key: "client_rejected", label: "Recusados" },
  { key: "resolved", label: "Resolvidos" },
  { key: "all", label: "Todos" },
];

/**
 * AdminIssues — admin-side triage of problems reported by employees.
 * Shows a pulsing "open" badge while there are unresolved issues. Lets the
 * admin acknowledge (signal to the employee that the issue was read) or
 * resolve it. Links to the originating booking for full context.
 */
export default function AdminIssues() {
  const [filter, setFilter] = useState("open");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/admin/issues", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  async function setStatus(id, status) {
    try {
      await api.patch(`/admin/issues/${id}`, { status });
      toast.success(status === "resolved" ? "Problema resolvido" : "Estado atualizado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function sendToClient(id, adminValue, adminNote) {
    const n = parseFloat(adminValue);
    if (!Number.isFinite(n) || n <= 0) { toast.error("Define um valor maior que 0."); return; }
    try {
      await api.patch(`/admin/issues/${id}/review`, {
        admin_value: n,
        admin_note: adminNote || null,
        send_to_client: true,
      });
      toast.success("Enviado ao cliente para aprovação.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function onBehalf(id, decision) {
    const label = decision === "approve" ? "aprovar" : "recusar";
    if (!window.confirm(`Confirmas que o cliente quis ${label} por telefone?`)) return;
    try {
      await api.post(`/admin/approvals/issue/${id}/on-behalf`, { decision });
      toast.success(decision === "approve" ? "Aprovado em nome do cliente." : "Recusado em nome do cliente.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function deleteIssue(id) {
    if (!window.confirm("Eliminar este problema? Esta ação é irreversível.")) return;
    try {
      await api.delete(`/admin/issues/${id}`);
      toast.success("Problema eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const items = data?.items || [];
  const ids = useMemo(() => items.map(i => i.id), [items]);
  const bulk = useBulkSelect(ids);
  async function bulkRun(selected, fn, { successMsg }) {
    const res = await Promise.allSettled(selected.map(fn));
    const ok = res.filter(r => r.status === "fulfilled").length;
    const ko = res.length - ok;
    if (ok > 0) toast.success(`${ok} ${successMsg}${ko > 0 ? ` · ${ko} falharam` : ""}`);
    else toast.error("Nenhuma operação concluída");
    load();
  }
  const openCount = data?.open_count || 0;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-3">Operação</div>
          <h1 className="font-display text-5xl">Problemas reportados.</h1>
          <div className="text-sm text-[#a0b2af] mt-2 max-w-2xl">
            Problemas que os seus funcionários reportaram durante as limpezas. Marque como recebido ou resolvido.
          </div>
        </div>
        {openCount > 0 && (
          <div className="px-4 py-2 border border-[#f59e0b] text-[#f59e0b] bg-[#f59e0b]/5 rounded-sm animate-pulse text-xs uppercase tracking-widest"
               data-testid="open-issues-badge">
            {openCount} {openCount === 1 ? "problema em aberto" : "problemas em aberto"}
          </div>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2" data-testid="issues-filters">
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
                  className={`px-4 py-2 text-xs uppercase tracking-widest border rounded-sm ${filter === f.key ? "border-[#d4af37] text-[#d4af37] bg-[#062c27]" : "border-[#1a4640] text-[#a0b2af]"}`}
                  data-testid={`issue-filter-${f.key}`}>{f.label}</button>
        ))}
      </div>

      {loading && <div className="text-[#a0b2af] text-sm">A carregar…</div>}

      {!loading && items.length === 0 && (
        <div className="lux-card p-10 text-center text-sm text-[#a0b2af]" data-testid="issues-empty">
          {filter === "open" ? "🎉 Zero problemas em aberto. Continue o bom trabalho!" : "Sem problemas neste estado."}
        </div>
      )}

      <div className="space-y-3">
        {items.length > 0 && (
          <label className="inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-widest text-[#a0b2af] border border-[#1a4640] rounded-sm w-fit cursor-pointer hover:text-[#d4af37] hover:border-[#d4af37]/50">
            <BulkCheckbox
              checked={bulk.allSelected}
              indeterminate={bulk.someSelected}
              onChange={() => bulk.toggleAll(ids)}
              label="Selecionar todos"
            />
            Selecionar todos
          </label>
        )}
        {items.map(it => (
          <div key={it.id} className="relative">
            <div className="absolute top-5 right-5 z-10">
              <BulkCheckbox
                id={it.id}
                checked={bulk.isSelected(it.id)}
                onChange={() => bulk.toggle(it.id)}
                label="Selecionar problema"
              />
            </div>
            <IssueCard it={it} onStatus={setStatus} onDelete={deleteIssue} onSend={sendToClient} onBehalf={onBehalf} />
          </div>
        ))}
      </div>

      <BulkActionBar
        count={bulk.count}
        ids={Array.from(bulk.selected)}
        onClear={bulk.clear}
        actions={[
          {
            key: "resolve", label: "Resolver", icon: CheckCircle2, variant: "gold",
            confirm: "Marcar {count} problema(s) como resolvido(s)?",
            onClick: (sel) => bulkRun(sel, (id) => api.patch(`/admin/issues/${id}`, { status: "resolved" }), { successMsg: "resolvido(s)" }),
          },
          {
            key: "ack", label: "Reconhecer", icon: Eye, variant: "default",
            confirm: "Reconhecer {count} problema(s)?",
            onClick: (sel) => bulkRun(sel, (id) => api.patch(`/admin/issues/${id}`, { status: "acknowledged" }), { successMsg: "reconhecido(s)" }),
          },
          {
            key: "delete", label: "Eliminar", icon: Trash2, variant: "danger",
            confirm: "Eliminar {count} problema(s)? Ação irreversível.",
            onClick: (sel) => bulkRun(sel, (id) => api.delete(`/admin/issues/${id}`), { successMsg: "eliminado(s)" }),
          },
        ]}
      />
    </div>
  );
}

function IssueCard({ it, onStatus, onDelete, onSend, onBehalf }) {
  const st = STATUS_STYLES[it.status] || STATUS_STYLES.open;
  const Ic = st.icon;
  const [adminValue, setAdminValue] = useState(it.admin_value ?? "");
  const [adminNote, setAdminNote] = useState(it.admin_note ?? "");
  const canReview = ["open", "acknowledged"].includes(it.status);
  const photos = it.photos || [];

  return (
    <div className={`lux-card p-5 border-l-4 ${st.border}`} data-testid={`admin-issue-${it.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`text-xs uppercase tracking-widest px-2 py-0.5 border ${st.border} ${st.color} ${st.bg} inline-flex items-center gap-1`}>
              <Ic size={11} /> {st.label}
            </span>
            <span className="text-xs text-[#d4af37] uppercase tracking-widest">
              {CATEGORY_LABEL[it.category] || it.category}
            </span>
            {typeof it.admin_value === "number" && it.admin_value > 0 && (
              <span className="text-xs text-[#d4af37]">• {Number(it.admin_value).toFixed(2)} €</span>
            )}
          </div>
          <div className="mt-3 text-sm">{it.description}</div>
          {it.client_note && (
            <div className="mt-2 text-xs text-[#a0b2af] italic">Resposta do cliente: “{it.client_note}”</div>
          )}
          <div className="mt-3 flex flex-wrap gap-4 text-[11px] text-[#a0b2af]">
            <span className="inline-flex items-center gap-1">
              <User size={11} className="text-[#d4af37]" /> {it.employee_name}
            </span>
            <span className="inline-flex items-center gap-1">
              <Calendar size={11} className="text-[#d4af37]" />
              {format(parseISO(it.created_at), "d 'de' MMM yyyy 'às' HH:mm", { locale: pt })}
            </span>
          </div>

          {photos.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {photos.map(p => (
                <a key={p.id} href={`${BACKEND_URL}/api/files/${p.storage_path}`}
                   target="_blank" rel="noreferrer"
                   className="w-16 h-16 border border-[#1a4640] rounded-sm overflow-hidden hover:border-[#d4af37] transition-colors">
                  <img src={`${BACKEND_URL}/api/files/${p.storage_path}`}
                       alt="foto problema" className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0 flex-wrap">
          {it.status === "open" && (
            <button onClick={() => onStatus(it.id, "acknowledged")}
                    className="btn-ghost-gold px-3 py-1.5 text-xs inline-flex items-center gap-1"
                    data-testid={`acknowledge-${it.id}`}>
              <Eye size={12} /> Recebido
            </button>
          )}
          {["client_approved", "pending_client"].includes(it.status) && (
            <button onClick={() => onStatus(it.id, "resolved")}
                    className="btn-gold px-3 py-1.5 text-xs inline-flex items-center gap-1"
                    data-testid={`resolve-${it.id}`}>
              <CheckCircle2 size={12} /> Marcar resolvido
            </button>
          )}
          {it.status === "pending_client" && (
            <>
              <button onClick={() => onBehalf(it.id, "approve")}
                      className="px-3 py-1.5 text-xs text-[#10b981] border border-[#10b981]/40 hover:bg-[#10b981]/10 inline-flex items-center gap-1"
                      data-testid={`on-behalf-approve-${it.id}`}
                      title="Cliente aprovou por telefone">
                <CheckCircle2 size={12} /> Aprovar (tel.)
              </button>
              <button onClick={() => onBehalf(it.id, "reject")}
                      className="px-3 py-1.5 text-xs text-[#fca5a5] border border-[#fca5a5]/40 hover:bg-[#fca5a5]/10 inline-flex items-center gap-1"
                      data-testid={`on-behalf-reject-${it.id}`}
                      title="Cliente recusou por telefone">
                <XCircle size={12} /> Recusar (tel.)
              </button>
            </>
          )}
          {it.status === "resolved" && (
            <button onClick={() => onStatus(it.id, "open")}
                    className="text-xs text-[#a0b2af] border border-[#1a4640] px-3 py-1.5 hover:text-white"
                    data-testid={`reopen-${it.id}`}>
              Reabrir
            </button>
          )}
          <button onClick={() => onDelete(it.id)}
                  className="text-xs text-[#fca5a5] border border-[#ef4444]/60 px-3 py-1.5 hover:bg-[#ef4444]/10 transition-colors rounded-sm inline-flex items-center gap-1"
                  data-testid={`delete-issue-${it.id}`}
                  title="Eliminar problema (irreversível)">
            <Trash2 size={12} /> Eliminar
          </button>
        </div>
      </div>

      {canReview && (
        <div className="mt-4 pt-4 border-t border-[#1a4640] grid grid-cols-1 md:grid-cols-3 gap-3 items-end" data-testid={`review-${it.id}`}>
          <label className="text-xs text-[#a0b2af] block md:col-span-1">
            <span className="uppercase tracking-widest">Valor a cobrar (€)</span>
            <input type="number" step="0.01" min="0" value={adminValue}
                   onChange={e => setAdminValue(e.target.value)}
                   placeholder="0.00"
                   className="mt-1 w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-3 py-2 text-sm text-[#fdfbf7]"
                   data-testid={`value-${it.id}`} />
          </label>
          <label className="text-xs text-[#a0b2af] block md:col-span-1">
            <span className="uppercase tracking-widest">Nota ao cliente (opcional)</span>
            <input value={adminNote}
                   onChange={e => setAdminNote(e.target.value)}
                   placeholder="Ex: peça orçamentada na oficina X"
                   className="mt-1 w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-3 py-2 text-sm text-[#fdfbf7]"
                   data-testid={`note-${it.id}`} />
          </label>
          <button onClick={() => onSend(it.id, adminValue, adminNote)}
                  className="btn-gold px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2"
                  data-testid={`send-${it.id}`}>
            <Send size={12} /> Enviar ao cliente
          </button>
        </div>
      )}
    </div>
  );
}
